import React, { useState } from "react";
import { Eye, FileText, Mail, MapPin, Phone } from "lucide-react";
import { elakayAutoText } from "../dataElakay";
import { APPLICATION_DOCUMENT_TYPE_LABELS, formatFileSize } from "./jobApplicationFormat";
import JobDocumentPreviewModal from "./JobDocumentPreviewModal";
import "../../pages/jobDashboard.css";

// Read-only rendering of a single job application: identity, CV / Resume /
// LinkedIn / Website / Portfolio + the candidate's custom links (rendered in
// the exact same .job-admin-applicant-links row/style), cover letter, and
// documents. Shared by the admin Job Dashboard applicant modal
// (jobDashboard.jsx) and the candidate's own "View Application" on
// /my-job-applications so both surfaces stay visually identical.
//
// Every link/document opens the shared in-app JobDocumentPreviewModal
// instead of navigating to a new tab -- same behavior as
// jobApplicationsAdmin.jsx's applicant modal, the user always stays on the
// current page. The modal itself still offers an explicit "open in a new
// tab" fallback for sites that refuse to be framed.
const JobApplicationView = ({ application }) => {
  const [previewItem, setPreviewItem] = useState(null);
  const openPreview = (title, url) => setPreviewItem({ title, url });
  const closePreview = () => setPreviewItem(null);

  if (!application) return null;

  const hasCoreLinks = application.resumeUrl || application.portfolioUrl || application.linkedinUrl;
  const socialLinks = Array.isArray(application.socialLinks) ? application.socialLinks : [];
  const documents = Array.isArray(application.documents) ? application.documents : [];

  return (
    <div className="job-admin-applicant-view">
      <h2>{application.fullName}</h2>
      <div className="job-admin-applicant-meta">
        <span><Mail size={14} />{application.email}</span>
        {application.phone && <span><Phone size={14} />{application.phone}</span>}
        {application.location && <span><MapPin size={14} />{application.location}</span>}
        <span className="job-admin-pill">{application.status}</span>
      </div>

      {hasCoreLinks && (
        <div className="job-admin-applicant-links">
          {application.resumeUrl && (
            <button type="button" onClick={() => openPreview(elakayAutoText("h_8963b0f409c3a3", "CV"), application.resumeUrl)}>
              <Eye size={14} />{elakayAutoText("h_8963b0f409c3a3", "CV")}
            </button>
          )}
          {application.portfolioUrl && (
            <button type="button" onClick={() => openPreview(elakayAutoText("h_jobapp_portfolio", "Portfolio"), application.portfolioUrl)}>
              <Eye size={14} />{elakayAutoText("h_jobapp_portfolio", "Portfolio")}
            </button>
          )}
          {application.linkedinUrl && (
            <button type="button" onClick={() => openPreview(elakayAutoText("h_jobapp_linkedin", "LinkedIn"), application.linkedinUrl)}>
              <Eye size={14} />{elakayAutoText("h_jobapp_linkedin", "LinkedIn")}
            </button>
          )}
        </div>
      )}

      {socialLinks.length > 0 && (
        <div className="job-admin-applicant-links">
          {socialLinks.map((link, index) => (
            <button
              key={`${link.url}-${index}`}
              type="button"
              onClick={() => openPreview(link.platform || elakayAutoText("h_job_social_links_label", "Reseaux sociaux"), link.url)}
            >
              <Eye size={14} />{link.platform || elakayAutoText("h_job_social_links_label", "Reseaux sociaux")}
            </button>
          ))}
        </div>
      )}

      {application.coverLetter && (
        <div className="job-admin-applicant-cover">
          <h3>{elakayAutoText("h_39cd7fc6843107", "Message de candidature")}</h3>
          <p>{application.coverLetter}</p>
        </div>
      )}

      <h3>{elakayAutoText("h_687c82861c956e", "Documents")}</h3>
      {documents.length === 0 ? (
        <div className="job-admin-empty small">{elakayAutoText("h_jobapp_nodocs", "Aucun document joint.")}</div>
      ) : (
        <div className="job-admin-documents">
          {documents.map((document, index) => (
            <div className="job-admin-document" key={document._id || `${document.url}-${index}`}>
              <div>
                <strong><FileText size={15} /> {document.title || document.originalName || elakayAutoText("h_e214b8a2992305", "Document")}</strong>
                <span>
                  {APPLICATION_DOCUMENT_TYPE_LABELS[document.documentType] || document.documentType} ·{" "}
                  {String(document.kind || "file").toUpperCase()} {formatFileSize(document.size)}
                </span>
              </div>
              <div className="job-admin-project-actions">
                <button
                  className="job-admin-btn secondary"
                  type="button"
                  onClick={() => openPreview(document.title || document.originalName || elakayAutoText("h_e214b8a2992305", "Document"), document.url)}
                >
                  <Eye size={14} />{elakayAutoText("h_42c07747756067", "Ouvrir")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <JobDocumentPreviewModal item={previewItem} onClose={closePreview} />
    </div>
  );
};

export default JobApplicationView;
